import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./OrderDetailPage.css";

/**
 * OrderDetail page
 * - GET /api/orders/:id (expects items, payments, shipping, user, totals)
 * - POST /api/payment/start  -> start payment flow (body { order_id })
 * - DELETE /api/orders/:id  -> delete/cancel pending order
 * - GET  /api/orders/:id/invoice -> download invoice (optional)
 *
 * Be defensive: backend may return different shapes; this component normalizes.
 */

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.goharesadaf.ir/api";

const statusMap = {
  paid: "پرداخت شده",
  pending: "در انتظار پرداخت",
  initiated: "شروع شده",
  failed: "ناموفق",
  canceled: "لغو شده",
  completed: "تکمیل شده",
  shipped: "ارسال شده",
  processing: "در حال پردازش",
};

const statusClass = (s) => {
  if (!s) return "unknown";
  const v = String(s).toLowerCase();
  if (["paid", "settled", "completed"].includes(v)) return "paid";
  if (["pending", "initiated"].includes(v)) return "pending";
  if (["failed"].includes(v)) return "failed";
  if (["canceled"].includes(v)) return "canceled";
  if (["shipped"].includes(v)) return "shipped";
  return "unknown";
};

function formatAmount(n) {
  return Number(n || 0).toLocaleString("fa-IR");
}

function formatDate(d) {
  try {
    return new Date(d).toLocaleString("fa-IR");
  } catch {
    return d || "";
  }
}

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState({}); // toggle for payment raw responses
  const [refreshKey, setRefreshKey] = useState(0);
  const STORAGE_BASE = "https://api.goharesadaf.ir/storage/";

  useEffect(() => {     
    if (!id) {
      setError("شناسه سفارش مشخص نیست.");
      setLoading(false);
      return;
    }
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refreshKey]);

  async function fetchOrder() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/orders/${id}`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (res.status === 401) {
        localStorage.removeItem("token");
        setError("لطفاً ابتدا وارد شوید.");
        setOrder(null);
        setLoading(false);
        return;
      }
      if (!res.ok) {
        const txt = await res.text().catch(()=>null);
        throw new Error(txt || `خطا در دریافت سفارش (${res.status})`);
      }
      const json = await res.json();
      // normalize: if backend wraps with {order: {...}} etc.
      const ord = json?.order || json?.data || json;
      setOrder(ord);
    } catch (err) {
      console.error("fetchOrder err:", err);
      setError(err.message || "خطا در دریافت سفارش");
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }


  const handlePay = async () => {
    try {
      const startRes = await fetch("https://api.goharesadaf.ir/api/payment/start", {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ order_id: order.id }),
      });

      if (!startRes.ok) {
        const err = await startRes.json().catch(()=>({}));
        alert(err.message || "Error starting payment.");
        return;
      }

      const { ref, start_url } = await startRes.json();

      // 3) auto-post the RefId to Mellat
      const form = document.createElement("form");
      form.method = "POST";
      form.action = start_url || "https://bpm.shaparak.ir/pgwchannel/startpay.mellat";
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "RefId";
      input.value = ref;
      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error(err);
      alert(err.message || "خطا در ثبت سفارش یا پرداخت");
    }
  };

  async function handleDelete() {
    if (!order) return;
    if (!window.confirm("آیا مطمئن هستید که می‌خواهید این سفارش را حذف کنید؟")) return;

    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/orders/${order.id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const txt = await res.text().catch(()=>null);
        throw new Error(txt || "امکان حذف سفارش وجود ندارد.");
      }
      alert("سفارش حذف شد.");
      navigate("/orders");
    } catch (err) {
      alert(err.message || "خطا در حذف سفارش");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDownloadInvoice() {
    if (!order) return;
    const url = `${API_BASE}/orders/${order.id}/invoice`;
    // try opening in new tab with token header by constructing a signed URL endpoint on server ideally.
    // Fallback: open in new tab (may require public endpoint)
    window.open(url, "_blank");
  }

  async function handleRequestRefund() {
    if (!order) return;
    if (!window.confirm("درخواست استرداد برای این سفارش ثبت شود؟")) return;

    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/orders/${order.id}/refund`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const b = await res.json().catch(()=>({}));
        throw new Error(b.message || "خطا در ثبت درخواست استرداد");
      }
      alert("درخواست استرداد ثبت شد.");
      setRefreshKey(k => k + 1);
    } catch (err) {
      alert(err.message || "خطا در درخواست استرداد");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRefresh() {
    setRefreshKey((k) => k + 1);
  }

  // Payment attempts (if supplied as order.payments or order.payment_history)
  const payments = order?.payments || order?.payment_history || order?.paymentAttempts || [];

  return (
    <div className="od-shell">
      <div className="od-header">
        <div className="od-left">
          <h1>سفارش #{order?.id?.toLocaleString?.("fa-IR") || id}</h1>
          <div className={`od-badge ${statusClass(order?.status)}`}>{statusMap[order?.status] || order?.status || "—"}</div>
          <div className="od-meta">
            <div><strong>تاریخ:</strong> {order?.created_at ? formatDate(order.created_at) : "—"}</div>
            <div><strong>کد تراکنش:</strong> {order?.transaction_id || "—"}</div>
          </div>
        </div>

        <div className="od-actions">
          <button className="btn-muted" onClick={handleRefresh} disabled={loading || actionLoading}>به‌روزرسانی</button>

          {order?.status === "pending" && (
            <>
              <button className="btn-primary" onClick={handlePay} disabled={actionLoading}>پرداخت</button>
              <button className="btn-quiet" onClick={handleDelete} disabled={actionLoading}>حذف سفارش</button>
            </>
          )}

          {["paid","settled","completed"].includes(String(order?.status || "").toLowerCase()) && (
            <>
              <button className="btn-primary-outline" onClick={handleDownloadInvoice}>دریافت فاکتور</button>
              <button className="btn-quiet" onClick={handleRequestRefund} disabled={actionLoading}>درخواست استرداد</button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="od-loading">در حال بارگذاری...</div>
      ) : error ? (
        <div className="od-error">{error}</div>
      ) : !order ? (
        <div className="od-empty">سفارشی یافت نشد.</div>
      ) : (
        <div className="od-grid">
          <section className="od-column od-overview">
            <h3>خلاصۀ سفارش</h3>
            <div className="od-card">
              <div className="od-row"><span>آدرس تحویل</span><span>{order?.shipping?.address || order?.address || "—"}</span></div>
              <div className="od-row"><span>نام گیرنده</span><span>{order?.shipping?.name || order?.recipient_name || order?.user?.name || "—"}</span></div>
              <div className="od-row"><span>تلفن</span><span>{order?.shipping?.phone || order?.user?.phone || "—"}</span></div>
              <div className="od-row"><span>روش ارسال</span><span>{order?.shipping?.method || order?.delivery_option || "—"}</span></div>
              <div className="od-row"><span>وضعیت پرداخت</span><span className={`od-small-badge ${statusClass(order?.status)}`}>{statusMap[order?.status] || order?.status}</span></div>
            </div>

            <h3 style={{marginTop:12}}>خلاصۀ هزینه</h3>
            <div className="od-card">
              <div className="od-row"><span>جمع قیمت کالاها</span><span>{formatAmount(order?.subtotal ?? order?.items?.reduce?.((s,it)=> s + (it.price * (it.quantity || 1)), 0) ?? 0)} تومان</span></div>
              <div className="od-row"><span>هزینه ارسال</span><span>{formatAmount(order?.shipping_cost ?? order?.shipping?.cost ?? 0)} تومان</span></div>
              <div className="od-row"><span>تخفیف</span><span>{formatAmount(order?.discount ?? 0)} تومان</span></div>
              <div className="od-row"><span>مالیات</span><span>{formatAmount(order?.tax ?? 0)} تومان</span></div>
              <div className="od-row total"><span>قابل پرداخت</span><span>{formatAmount(order?.total ?? order?.amount ?? 0)} تومان</span></div>
            </div>

            {order?.gift && (
              <>
                <h3 style={{marginTop:12}}>هدیه</h3>
                <div className="od-card">
                  <div className="gift">
                    <img src={order.gift.main_image || order.gift.image || ""} alt={order.gift.title || "هدیه"} />
                    <div>
                      <div className="gift-title">{order.gift.title}</div>
                      <div className="gift-price">{formatAmount(order.gift.price)} تومان</div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>

          <section className="od-column od-items">
            <h3>محصولات</h3>
            <div className="od-card items-card">
              {Array.isArray(order.items) && order.items.length ? (
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>محصول</th>
                      <th>قیمت</th>
                      <th>تعداد</th>
                      <th>جمع</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((it, idx) => {
                      const product = it.product || it.product_data || {};
                      return (
                        <tr key={it.id ?? idx}>
                          <td className="prod-cell">
                            <img   src={
                                        product.main_image 
                                        ? `${STORAGE_BASE}${product.main_image}` 
                                        : ""
                                    } />
                            <div className="prod-info">
                              <div className="prod-title">{product.title || it.name || "—"}</div>
                              <div className="prod-meta">{product.sku ? `SKU: ${product.sku}` : ""}</div>
                            </div>
                          </td>
                          <td>{formatAmount(it.price)} تومان</td>
                          <td>{Number(it.quantity).toLocaleString("fa-IR")}</td>
                          <td>{formatAmount((it.price || 0) * (it.quantity || 1))} تومان</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div>آیتمی برای نمایش موجود نیست.</div>
              )}
            </div>

            <h3 style={{marginTop:12}}>تاریخچه پرداخت‌ها</h3>
            <div className="od-card payments">
              {payments && payments.length ? (
                payments.map((p, idx) => (
                  <div key={p.id ?? idx} className="pay-row">
                    <div className="pay-left">
                      <div className="pay-code">#{p.id ?? (p.saleReferenceId || p.sale_reference_id || p.saleReferenceId)}</div>
                      <div className="pay-meta">{p.created_at ? formatDate(p.created_at) : (p.localDate ? `${p.localDate} ${p.localTime || ''}` : "")}</div>
                    </div>

                    <div className="pay-center">
                      <div className={`pay-status ${statusClass(p.status ?? p.ResCode ?? p.res_code ?? p.rescode)}`}>
                        {p.status || (p.ResCode ? (p.ResCode === 0 ? "settled" : `code:${p.ResCode}`) : "")}
                      </div>
                      <div className="pay-amount">{formatAmount(p.amount ?? order.amount ?? order.total ?? 0)} تومان</div>
                    </div>

                    <div className="pay-right">
                      <button className="btn-quiet small" onClick={() => setShowRaw(s => ({...s, [idx]: !s[idx]}))}>
                        {showRaw[idx] ? "پنهان" : "جزئیات"}
                      </button>
                    </div>

                    {showRaw[idx] && (
                      <pre className="pay-raw">{JSON.stringify(p, null, 2)}</pre>
                    )}
                  </div>
                ))
              ) : (
                <div className="muted">هیچ تراکنشی ثبت نشده است.</div>
              )}
            </div>

            <h3 style={{marginTop:12}}>لگ و وضعیت</h3>
            <div className="od-card timeline">
              <div className="timeline-item">
                <div className="ti-left">ثبت سفارش</div>
                <div className="ti-right">{formatDate(order.created_at)}</div>
              </div>
              {payments && payments.length ? payments.map((p, i) => (
                <div key={i} className="timeline-item">
                  <div className="ti-left">تلاش پرداخت #{i+1}</div>
                  <div className="ti-right">{p.created_at ? formatDate(p.created_at) : p.localDate || "—"}</div>
                </div>
              )) : null}
              {order?.shipped_at && (
                <div className="timeline-item">
                  <div className="ti-left">ارسال شد</div>
                  <div className="ti-right">{formatDate(order.shipped_at)}</div>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
